import { z } from "zod";
import {
  BRAZIL_UFS,
  GOAL_METRICS,
  GOAL_SCOPES,
  MAX_DASHBOARD_PCT4,
  MAX_DASHBOARD_STATUSES,
  MAX_GOAL_TARGET,
  dashboardPeriodProblem,
  goalProblem,
  isIsoDate,
  type DashboardSettingsValue,
  type GoalMetric,
  type GoalScope,
  type GoalState,
  type Kpi,
  type MonthForecast,
  type Uf,
} from "@cx/domain";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";
import type { SalesOrderStatusRef } from "./sales-orders";

// ===== Dashboards e metas (T1.13, RF-BI-01..09) =====

const nullish = <T extends z.ZodType>(s: T) => s.nullish().transform((v) => v ?? null);

/** `statuses` = ids separados por vírgula (até 30, sem repetir); ausente = as situações da configuração. */
const statuses = z
  .string()
  .max(MAX_DASHBOARD_STATUSES * 27 + 10)
  .transform((v) => v.split(",").filter(Boolean))
  .pipe(
    z
      .array(ulidParam)
      .min(1, "Escolha as situações")
      .max(MAX_DASHBOARD_STATUSES, `No máximo ${MAX_DASHBOARD_STATUSES} situações`)
      .refine((l) => new Set(l).size === l.length, "Situação repetida"),
  )
  .optional();

/** Par de datas: os dois ou nenhum, até 366 dias; a data que já falhou no formato não ganha um segundo erro. */
export function refinePair(
  v: Record<string, unknown>,
  ctx: z.RefinementCtx,
  fromKey: string,
  toKey: string,
  what: string,
) {
  const from = v[fromKey] as string | undefined;
  const to = v[toKey] as string | undefined;
  if (from === undefined && to === undefined) return;
  if (from === undefined)
    return ctx.addIssue({ code: "custom", path: [fromKey], message: `Informe o início ${what}` });
  if (to === undefined)
    return ctx.addIssue({ code: "custom", path: [toKey], message: `Informe o fim ${what}` });
  if (!isIsoDate(from) || !isIsoDate(to)) return;
  const p = dashboardPeriodProblem(from, to);
  if (p) ctx.addIssue({ code: "custom", path: [toKey], message: p });
}

/** Mensal (RF-BI-01..04, 06): período e comparação opcionais (padrão: mês até hoje × mesmos dias do mês anterior). */
export const dashboardQuery = z
  .object({
    from: businessDate.optional(),
    to: businessDate.optional(),
    compareFrom: businessDate.optional(),
    compareTo: businessDate.optional(),
    statuses,
  })
  .superRefine((v, ctx) => {
    refinePair(v, ctx, "from", "to", "do período");
    refinePair(v, ctx, "compareFrom", "compareTo", "da comparação");
  });
export type DashboardQuery = z.output<typeof dashboardQuery>;

/** Diário (RF-BI-07): dia (padrão hoje) e dia de comparação (padrão: mesmo dia da semana anterior). */
export const dailyDashboardQuery = z.object({
  date: businessDate.optional(),
  compareDate: businessDate.optional(),
  statuses,
});
export type DailyDashboardQuery = z.output<typeof dailyDashboardQuery>;

/** Estatística anual (RF-BI-04): ano (padrão o atual) × ano anterior. */
export const annualDashboardQuery = z.object({
  year: z.coerce
    .number("Ano inválido")
    .int("Ano inválido")
    .min(1901, "Ano inválido")
    .max(2199, "Ano inválido")
    .optional(),
  statuses,
});
export type AnnualDashboardQuery = z.output<typeof annualDashboardQuery>;

/** Pedidos de venda (RF-BI-08): período (padrão mês até hoje); `uf` filtra situação, produto e canal. */
export const salesOrdersReportQuery = z
  .object({
    from: businessDate.optional(),
    to: businessDate.optional(),
    uf: z.enum(BRAZIL_UFS, "UF inválida").optional(),
    statuses,
  })
  .superRefine((v, ctx) => refinePair(v, ctx, "from", "to", "do período"));
export type SalesOrdersReportQuery = z.output<typeof salesOrdersReportQuery>;

/** Vendedor/canal 24 meses (RF-BI-09): até o mês `month` (padrão o atual). */
export const salesRankingQuery = z.object({
  month: z
    .string()
    .regex(/^(19|2[01])\d{2}-(0[1-9]|1[0-2])$/, "Mês inválido")
    .refine((m) => m >= "1902-01", "Mês inválido")
    .optional(),
  statuses,
});
export type SalesRankingQuery = z.output<typeof salesRankingQuery>;

export const dashboardSettingsInput = z.object({
  saleStatusIds: z
    .array(ulidParam, "Escolha as situações que contam como venda")
    .min(1, "Escolha as situações que contam como venda")
    .max(MAX_DASHBOARD_STATUSES, `No máximo ${MAX_DASHBOARD_STATUSES} situações`)
    .refine((l) => new Set(l).size === l.length, "Situação repetida"),
  taxPct4: z
    .number("Informe o percentual")
    .int("Percentual inválido")
    .min(0, "Use um percentual entre 0% e 100%")
    .max(MAX_DASHBOARD_PCT4, "Use um percentual entre 0% e 100%"),
  salesFeePct4: z
    .number("Informe o percentual")
    .int("Percentual inválido")
    .min(0, "Use um percentual entre 0% e 100%")
    .max(MAX_DASHBOARD_PCT4, "Use um percentual entre 0% e 100%"),
});
export type DashboardSettingsInput = z.output<typeof dashboardSettingsInput>;
export type DashboardSettings = DashboardSettingsValue;

export const goalInput = z
  .object({
    name: z.string("Informe o nome").trim().min(1, "Informe o nome").max(60, "Até 60 caracteres"),
    metric: z.enum(GOAL_METRICS, "Escolha a métrica"),
    scope: z.enum(GOAL_SCOPES, "Escolha o escopo").default("company"),
    sellerId: nullish(ulidParam),
    target: z
      .number("Informe o alvo")
      .int("Informe o alvo")
      .min(1, "Informe o alvo")
      .max(Math.max(...Object.values(MAX_GOAL_TARGET)), "Alvo fora do limite"),
    periodStart: businessDate,
    periodEnd: businessDate,
  })
  .superRefine((v, ctx) => {
    // Campo que já falhou no próprio formato não ganha um segundo erro.
    if (!isIsoDate(v.periodStart) || !isIsoDate(v.periodEnd)) return;
    if (!Number.isSafeInteger(v.target) || v.target < 1 || !GOAL_METRICS.includes(v.metric)) return;
    const p = goalProblem(v);
    if (p) ctx.addIssue({ code: "custom", path: [p.field], message: p.message });
  });
export type GoalInput = z.output<typeof goalInput>;
export type GoalFormValues = z.input<typeof goalInput>;

export const goalListQuery = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  state: z.enum(["current", "future", "past", "all"], "Filtro inválido").default("all"),
});
export type GoalListQuery = z.output<typeof goalListQuery>;

// ---------- respostas ----------

export type DashboardOptions = { settings: DashboardSettings; statuses: SalesOrderStatusRef[] };

export type DashboardCards = { totalCents: Kpi; orders: Kpi; itemsQ4: Kpi; avgTicketCents: Kpi };

/**
 * DRE resumida: custo, margem e lucro só com products:read_cost (chaves ausentes sem ela). `costMissingOrders`: pedidos
 * do período com item sem custo (o custo fica subestimado).
 */
export type DashboardDre = {
  netRevenueCents: Kpi;
  taxesCents: Kpi;
  feesCents: Kpi;
  taxPct4: number;
  salesFeePct4: number;
  costCents?: Kpi;
  grossMarginPct4?: Kpi;
  profitCents?: Kpi;
  profitPct4?: Kpi;
  costMissingOrders?: number | null;
};

export type SalesPoint = { totalCents: number | null; orders: number | null };
export type ChannelRow = { key: string; label: string; totalCents: number | null; orders: number | null };
export type ChannelDailyRow = { key: string; label: string; values: (number | null)[] };
/** Produto (Top SKUs) ou produto pai (variações somadas no pai). Custo só com products:read_cost. */
export type ProductSalesRow = {
  productId: string;
  name: string;
  sku: string | null;
  qtyQ4: number | null;
  revenueCents: number | null;
  orders: number | null;
  costCents?: number | null;
};

export type GoalView = {
  id: string;
  name: string;
  metric: GoalMetric;
  scope: GoalScope;
  seller: { id: string; name: string } | null;
  target: number;
  periodStart: string;
  periodEnd: string;
  state: GoalState;
  /** Realizado pelas situações da configuração (null = acima de 2^53). */
  actual: number | null;
  progressPct4: number | null;
  /** Parte do período já decorrida: o ritmo esperado. */
  elapsedPct4: number;
  createdAt: string;
  updatedAt: string;
};
export type GoalListResult = { data: GoalView[]; nextCursor: string | null };

export type DashboardResult = {
  period: { from: string; to: string };
  compare: { from: string; to: string };
  statuses: SalesOrderStatusRef[];
  cards: DashboardCards;
  /** Série "Valor total de pedidos por dia": um ponto por dia de cada período (posição = dia relativo). */
  days: string[];
  compareDays: string[];
  daily: SalesPoint[];
  compareDaily: SalesPoint[];
  dre: DashboardDre;
  channels: ChannelRow[];
  /** Valor por canal por dia (mesmos `days`). */
  channelDaily: ChannelDailyRow[];
  topProducts: ProductSalesRow[];
  topParents: ProductSalesRow[];
  /** Previsão do fechamento: só quando o período é o mês corrente até hoje. */
  forecast: MonthForecast | null;
  /** Metas em andamento (só com goals:read). */
  goals?: GoalView[];
};

export type DailyDashboardResult = {
  date: string;
  compareDate: string;
  statuses: SalesOrderStatusRef[];
  cards: DashboardCards;
  dre: DashboardDre;
  /** 24 horas (0..23); `noHour` = pedidos com data retroativa (sem horário). */
  hours: { hour: number; current: SalesPoint; previous: SalesPoint }[];
  noHour: { current: SalesPoint; previous: SalesPoint };
};

export type AnnualPoint = SalesPoint & { itemsQ4: number | null };
export type AnnualDashboardResult = {
  year: number;
  statuses: SalesOrderStatusRef[];
  months: { month: number; current: AnnualPoint; previous: AnnualPoint }[];
  totals: { current: AnnualPoint; previous: AnnualPoint };
};

export type SalesOrdersReport = {
  from: string;
  to: string;
  uf: Uf | null;
  statuses: SalesOrderStatusRef[];
  /** Por UF (todas as situações escolhidas; "" = sem UF no cadastro). */
  byState: { uf: Uf | ""; totalCents: number | null; orders: number | null }[];
  /** Por situação: todas as situações do módulo com venda no período (ignora o filtro de situações). */
  byStatus: { status: SalesOrderStatusRef; totalCents: number | null; orders: number | null }[];
  byProduct: ProductSalesRow[];
  byChannel: ChannelRow[];
};

export type SalesRankingRow = {
  key: string;
  label: string;
  totalCents: number | null;
  orders: number | null;
  /** Valor por mês (mesma ordem de `months`). */
  series: (number | null)[];
};
export type SalesRankingResult = {
  months: string[];
  statuses: SalesOrderStatusRef[];
  rows: SalesRankingRow[];
};
